import { useEffect, useRef, useState } from "react"

import AddIcon from "@/assets/icons/add/medium.svg?react"
import ArrowDoubleIcon from "@/assets/icons/arrowDouble/medium.svg?react"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover.tsx"
import { cn } from "@/lib/utils.ts"

import useReportingYear from "./context.ts"
import YearSelectDialog from "./YearSelectDialog.tsx"

export const LATEST_REPORTING_YEAR = 2026
const REPORTING_YEAR_RANGE = 28
export const years = Array.from({ length: REPORTING_YEAR_RANGE }, (_, i) => LATEST_REPORTING_YEAR - i)

export default function YearSwitch() {
  const { reportingYear, setReportingYear } = useReportingYear()
  const [isPopoverOpen, setIsPopoverOpen] = useState(false)
  const [openModal, setOpenModal] = useState(false)
  const reportingYearRef = useRef<HTMLButtonElement | null>(null)

  useEffect(() => {
    const timeout = setTimeout(
      () => isPopoverOpen && reportingYearRef.current?.scrollIntoView({ behavior: "instant", block: "center" }),
      0,
    )
    return () => clearTimeout(timeout)
  }, [isPopoverOpen])

  if (reportingYear == null) return null

  return (
    <Popover open={isPopoverOpen} onOpenChange={setIsPopoverOpen}>
      <PopoverTrigger asChild>
        <button
          className={cn(
            "group relative flex grow-0 text-nowrap rounded-xl border border-neutral-border bg-white p-3 shadow-sm transition-all duration-300 hover:shadow-md",
          )}
        >
          <p className={cn("text-sm text-neutral-text group-hover:text-brand-primary")}>
            Reporting Year:{" "}
            <span
              className={cn(
                "inline-block min-w-11 text-left text-base font-medium text-neutral-title group-hover:text-brand-primary",
              )}
            >
              {reportingYear}
            </span>
          </p>
          <ArrowDoubleIcon className={cn("h-5 w-5 rotate-180 text-neutral-text group-hover:text-brand-primary")} />
        </button>
      </PopoverTrigger>
      <PopoverContent
        className={"rounded-xl p-0 data-[side=bottom]:slide-in-from-top-20 data-[side=bottom]:slide-out-to-top-10"}
        side={"bottom"}
        align={"end"}
      >
        <h6 className={"px-5 py-4 text-xs font-medium uppercase tracking-widest text-gray-500"}>reporting year</h6>
        <div className={"max-h-64 overflow-auto border-y border-neutral-border"}>
          {years.map((year) => (
            <button
              key={year}
              ref={Number(year) === Number(reportingYear) ? reportingYearRef : null}
              className={cn(
                "hover:bg-brand-main-100 block w-full px-5 py-4 text-left text-neutral-text transition-all hover:text-brand-primary",
                Number(year) === Number(reportingYear) &&
                  "pointer-events-none bg-neutral-disabled font-medium text-neutral-text",
              )}
              onClick={() => {
                setReportingYear(year)
                setIsPopoverOpen(false)
              }}
            >
              {year}
            </button>
          ))}
        </div>

        <button
          onClick={() => setOpenModal(true)}
          className={
            "hover:bg-brand-main-100 flex w-full items-center gap-2 px-5 py-4 text-left text-neutral-text transition-all hover:text-brand-primary"
          }
        >
          <AddIcon className={"h-4 w-4"} /> New reporting year
        </button>
        <YearSelectDialog isOpen={openModal} onClose={() => setOpenModal(false)} />
      </PopoverContent>
    </Popover>
  )
}
